// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Table, Card, Input, Select, Tag, Button, Space, message, Spin } from 'antd';
import { SearchOutlined, FilterOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Title } from '../components/design-system/typography';
import { StatusPill } from '../components/design-system/StatusPill';
import { colors, spacing } from '../components/design-system/tokens';

const { Search } = Input;
const { Option } = Select;

interface ApiShipment {
  id: string;
  reference: string;
  contract_reference: string;
  shipment_number: number;
  status: string;
  booking_number: string | null;
  vessel: string | null;
  container_count: number;
}

interface ShipmentData {
  id: string;
  contractRef: string;
  shipmentRef: string;
  supplier: string;
  buyer: string;
  vessel: string | null;
  pol: string; // Port of Loading
  pod: string; // Port of Discharge
  etd: string;
  eta: string;
  status: 'PLANNED' | 'IN_TRANSIT' | 'ARRIVED' | 'DELIVERED' | 'CANCELLED';
  containers: number;
  commodity: string;
  bookingNumber: string | null;
  daysRemaining?: number;
}

interface ShipmentsApiResponse {
  shipments: ApiShipment[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// API function to fetch shipments
const fetchShipments = async (): Promise<ShipmentData[]> => {
  const response = await fetch('/api/v1/shipments', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch shipments');
  }

  const data: ShipmentsApiResponse = await response.json();
  
  // Transform API response to match frontend interface
  return data.shipments.map(shipment => ({
    id: shipment.id,
    contractRef: shipment.contract_reference,
    shipmentRef: shipment.reference,
    supplier: 'TBD', // TODO: Get from contract data
    buyer: 'TBD', // TODO: Get from contract data
    vessel: shipment.vessel,
    pol: 'TBD', // TODO: Get from port data
    pod: 'TBD', // TODO: Get from port data
    etd: 'TBD', // TODO: Get from shipment dates
    eta: 'TBD', // TODO: Get from shipment dates
    status: (shipment.status.toUpperCase() as ShipmentData['status']) || 'PLANNED',
    containers: shipment.container_count,
    commodity: 'Almonds', // TODO: Get from contract/goods data
    bookingNumber: shipment.booking_number,
    daysRemaining: undefined, // TODO: Calculate from dates
  }));
};

const ShipmentsList: React.FC = () => {
  const navigate = useNavigate();
  const [filteredShipments, setFilteredShipments] = useState<ShipmentData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Use React Query to fetch shipments
  const { data: shipments = [], isLoading, error } = useQuery({
    queryKey: ['shipments'],
    queryFn: fetchShipments,
  });

  useEffect(() => {
    filterShipments();
  }, [shipments, searchText, statusFilter]);

  // Handle error state
  if (error) {
    message.error('Failed to load shipments');
  }

  const filterShipments = () => {
    let filtered = shipments;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(shipment => shipment.status === statusFilter);
    }

    // Filter by search text
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(shipment =>
        shipment.contractRef.toLowerCase().includes(search) ||
        shipment.shipmentRef.toLowerCase().includes(search) ||
        shipment.supplier.toLowerCase().includes(search) ||
        shipment.buyer.toLowerCase().includes(search) ||
        shipment.vessel.toLowerCase().includes(search) ||
        shipment.commodity.toLowerCase().includes(search) ||
        shipment.pol.toLowerCase().includes(search) ||
        shipment.pod.toLowerCase().includes(search)
      );
    }

    setFilteredShipments(filtered);
  };

  const getStatusVariant = (status: ShipmentData['status']) => {
    switch (status) {
      case 'DELIVERED':
        return 'positive';
      case 'IN_TRANSIT':
        return 'warning';
      case 'ARRIVED':
        return 'positive';
      case 'PLANNED':
        return 'neutral';
      case 'CANCELLED':
        return 'negative';
      default:
        return 'neutral';
    }
  };

  const getStatusText = (status: ShipmentData['status']) => {
    switch (status) {
      case 'PLANNED':
        return 'Planned';
      case 'IN_TRANSIT':
        return 'In Transit';
      case 'ARRIVED':
        return 'Arrived';
      case 'DELIVERED':
        return 'Delivered';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const columns: ColumnsType<ShipmentData> = [
    {
      title: 'Shipment Reference',
      dataIndex: 'shipmentRef',
      key: 'shipmentRef',
      width: 150,
      render: (text: string, record: ShipmentData) => (
        <div>
          <div style={{ fontWeight: 600, color: colors.text.primary }}>
            {text}
          </div>
          <div style={{ fontSize: '12px', color: colors.text.secondary }}>
            {record.contractRef}
          </div>
        </div>
      ),
    },
    {
      title: 'Supplier → Buyer',
      key: 'parties',
      width: 250,
      render: (_, record: ShipmentData) => (
        <div>
          <div style={{ fontWeight: 500, color: colors.text.primary }}>
            {record.supplier}
          </div>
          <div style={{ fontSize: '12px', color: colors.text.secondary, margin: '2px 0' }}>
            ↓
          </div>
          <div style={{ fontSize: '13px', color: colors.text.secondary }}>
            {record.buyer}
          </div>
        </div>
      ),
    },
    {
      title: 'Route',
      key: 'route',
      width: 200,
      render: (_, record: ShipmentData) => (
        <div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            {record.pol}
          </div>
          <div style={{ fontSize: '12px', color: colors.text.secondary, margin: '2px 0' }}>
            ↓
          </div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            {record.pod}
          </div>
        </div>
      ),
    },
    {
      title: 'Vessel & Dates',
      key: 'vessel',
      width: 180,
      render: (_, record: ShipmentData) => (
        <div>
          <div style={{ fontWeight: 500, color: colors.text.primary, fontSize: '13px' }}>
            {record.vessel}
          </div>
          <div style={{ fontSize: '12px', color: colors.text.secondary, marginTop: '4px' }}>
            ETD: {formatDate(record.etd)}
          </div>
          <div style={{ fontSize: '12px', color: colors.text.secondary }}>
            ETA: {formatDate(record.eta)}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: ShipmentData['status'], record: ShipmentData) => (
        <div>
          <StatusPill variant={getStatusVariant(status)}>
            {getStatusText(status)}
          </StatusPill>
          {record.daysRemaining && (
            <div style={{ 
              fontSize: '11px', 
              color: colors.text.secondary,
              marginTop: '4px' 
            }}>
              {record.daysRemaining} days
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Commodity',
      key: 'details',
      width: 150,
      render: (_, record: ShipmentData) => (
        <div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            {record.commodity}
          </div>
          <div style={{ fontSize: '12px', color: colors.text.secondary, marginTop: '2px' }}>
            {record.containers} container{record.containers !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '11px', color: colors.text.tertiary }}>
            {record.bookingNumber || 'TBD'}
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record: ShipmentData) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/shipments/${record.id}`)}
            title="View Details"
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/shipments/${record.id}/edit`)}
            title="Edit Shipment"
          />
        </Space>
      ),
    },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'PREPARING', label: 'Preparing' },
    { value: 'IN_TRANSIT', label: 'In Transit' },
    { value: 'ARRIVED', label: 'Arrived' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  const getStatusCounts = () => {
    const counts = {
      total: shipments.length,
      preparing: shipments.filter(s => s.status === 'PREPARING').length,
      inTransit: shipments.filter(s => s.status === 'IN_TRANSIT').length,
      arrived: shipments.filter(s => s.status === 'ARRIVED').length,
      delivered: shipments.filter(s => s.status === 'DELIVERED').length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div style={{
      background: colors.background.primary,
      minHeight: 'calc(100vh - 112px)',
      padding: spacing['2xl']
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{ marginBottom: spacing['2xl'] }}>
          <Title>Shipments</Title>
          <p style={{
            color: colors.text.secondary,
            fontSize: '16px',
            marginTop: spacing.sm
          }}>
            Track and manage all shipments across your contracts
          </p>
        </div>

        {/* Status Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: spacing.lg,
          marginBottom: spacing['2xl']
        }}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: colors.text.primary }}>
                {statusCounts.total}
              </div>
              <div style={{ color: colors.text.secondary }}>Total Shipments</div>
            </div>
          </Card>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: colors.status.warning }}>
                {statusCounts.inTransit}
              </div>
              <div style={{ color: colors.text.secondary }}>In Transit</div>
            </div>
          </Card>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: colors.status.neutral }}>
                {statusCounts.preparing}
              </div>
              <div style={{ color: colors.text.secondary }}>Preparing</div>
            </div>
          </Card>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: colors.status.positive }}>
                {statusCounts.arrived}
              </div>
              <div style={{ color: colors.text.secondary }}>Arrived</div>
            </div>
          </Card>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: colors.status.positive }}>
                {statusCounts.delivered}
              </div>
              <div style={{ color: colors.text.secondary }}>Delivered</div>
            </div>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card style={{ marginBottom: spacing.lg }}>
          <div style={{
            display: 'flex',
            gap: spacing.lg,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search shipments, suppliers, vessels..."
              allowClear
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
              suffixIcon={<FilterOutlined />}
            >
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>

            <div style={{ marginLeft: 'auto' }}>
              <Button 
                type="primary" 
                onClick={() => navigate('/shipments/create')}
              >
                Create Shipment
              </Button>
            </div>
          </div>
        </Card>

        {/* Shipments Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredShipments}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} shipments`,
            }}
            scroll={{ x: 1200 }}
            size="middle"
          />
        </Card>
      </div>
    </div>
  );
};

export default ShipmentsList;